import React, { useState } from "react";
import CustomButton from "../../components/Form/CustomButton";
import StarRating from "../../components/Form/StarRating";
import LocatioPinIcon from "../../assets/icons/location-pin.png";
import { useNavigate } from "react-router-dom";
import SubcitiesPopover from "./SubcitiesPopover";
import SlidingTabs from "../Common/SlidingTabs";
import ProfileImage from "../Common/ProfileImage";
import { useSelector } from "react-redux";
import showToast from "../../utils/toastNotifications";
import { useThunk } from "../../hooks/useThunk";
import { createThread } from "../../store/thunks/threadThunks";
import { ErrorCodes } from "../../constants/errorCodes";
import { twMerge } from "tailwind-merge";

const ServiceProCard = ({ servicePro, loading = false }) => {
  const userState = useSelector((state) => state?.auth?.user);
  const navigate = useNavigate();

  // Extract data from servicePro
  const professionalDetails = servicePro.professionalDetails?.primary || [];
  const servingAreas = servicePro.servingAreas?.primary || [];
  const cityName = servingAreas[0]?.cityName || "Unknown City";
  const subCities = servingAreas[0]?.subcities || [];

  // Set default category and skill
  const [activeCategory, setActiveCategory] = useState(professionalDetails[0]);
  const [activeSkills, setActiveSkills] = useState(
    professionalDetails[0]?.skills || []
  );

  // defining thunks
  const [doCreateThread, isCreateThread, errorCreateThread] =
    useThunk(createThread);

  // Handle category selection to update skills
  const handleCategorySelect = (category) => {
    setActiveCategory(category);
    setActiveSkills(category?.skills || []);
  };

  // createing a new chat between two unique users
  const handleStartChat = async (user2Id) => {
    if (user2Id) {
      const response = await doCreateThread(user2Id);
      if (response?.success) {
        // showToast("success", "Thread Created Successfully!");
        navigate(`/chat?thread=${response?.response?.data?._id}`);
      } else {
        if (response?.error?.customCode === ErrorCodes.DUPLICATE_ENTRIES.code) {
          navigate(
            `/chat?thread=${response?.error?.message
              ?.split(":")[1]
              ?.toString()
              ?.trim()}`
          );
        } else {
          showToast("error", response?.error?.message || "Error Occured");
        }
      }
    } else {
      showToast("error", "User ID Not Provided");
    }
  };

  return (
    <div className="flex flex-col w-full font-inter lg:flex-row p-4 sm:p-9 bg-white rounded-2xl border border-light-blue gap-4">
      {/* Left Section */}
      <div className="w-full lg:w-[50%] lg:max-w-[571px]">
        {/* User main details */}
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-[34px]">
          <div className="flex-shrink-0 w-32 h-32 md:w-40 md:h-40 rounded-full object-cover">
            <ProfileImage
              profilePicture={servicePro?.profilePicture}
              firstName={servicePro?.firstName}
              lastName={servicePro?.lastName}
              fontSize="md:text-[42px] text-[32px]"
              backgroundColor="#FBC948"
              loading={loading}
            />
          </div>
          <div className="py-4 sm:py-6">
            <div className="flex items-center gap-1.5 mb-2 md:mb-0 justify-center sm:justify-start">
              <StarRating rating={servicePro.avgRatingValue || 0} />
            </div>
            <h2
              className={twMerge(
                "text-level-3 leading-7 md:text-level-2 font-semibold font-inter md:leading-[48px] tracking-[0.032em] text-center sm:text-left",
                loading
                  ? "rounded-xl mt-2 mb-2 text-transparent bg-slate-300 animate-pulse"
                  : "text-primary"
              )}
            >
              {loading && "Loading State"}
              {servicePro.firstName} {servicePro.lastName}
            </h2>
            <div className="flex items-center gap-1 justify-center sm:justify-start ">
              {!loading && (
                <div className="flex flex-col items-start w-full">
                  <div className="flex gap-0 ">
                    <img src={LocatioPinIcon} alt="Location" className="w-5 h-5" />
                    <SubcitiesPopover cityName={cityName} subCities={subCities} />
                  </div>
                  <div className=" text-content font-inter text-level-7 font-medium leading-6 bg-gray-100 px-2 py-0 rounded-md shadow-sm">{`Total Orders: ${servicePro?.totolCompletedOrders ?? 0}`}</div>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mt-4">
          <CustomButton
            className="bg-primary text-level-5 text-white font-inter font-semibold h-12 w-full sm:w-[214px] flex items-center justify-center rounded-xl hover:bg-opacity-90 transition-colors"
            loaderColor="white"
            loading={isCreateThread}
            pulse={loading}
            buttonText="Start Chat"
            onClick={() => {
              handleStartChat(servicePro?._id?.toString());
            }}
            disabled={
              servicePro?._id?.toString() === userState?._id?.toString()
            }
          />
          <CustomButton
            className="bg-white text-level-5 text-primary font-inter border border-primary font-semibold h-12 w-full sm:w-[214px] flex items-center justify-center rounded-xl hover:bg-opacity-90 transition-colors"
            loaderColor="white"
            loading={false}
            pulse={loading}
            buttonText="View Profile"
            onClick={() => navigate(`/client/tasker-profile/${servicePro._id}`)}
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex flex-col gap-6 w-full lg:w-[50%] flex-1 mt-6 md:mt-0">
        {/* About Me */}
        <div
          className={twMerge(
            "flex flex-col gap-1.5",
            loading
              ? "rounded-xl w-[60%] h-14 text-transparent bg-slate-300 animate-pulse"
              : ""
          )}
        >
          {loading && "Loading state"}
          {!loading && (
            <h3 className="text-dark font-inter font-semibold text-level-5 tracking-[0.001em]">
              About Me
            </h3>
          )}
          {!loading && (
            <p className="text-dark font-inter font-normal text-level-6 leading-relaxed tracking-[0.001em] line-clamp-3">
              {servicePro.aboutMe?.primary ||
                "No details provided about this professional."}
            </p>
          )}
        </div>

        {/* Categories */}
        <div
          className={twMerge(
            "flex flex-col gap-1.5",
            loading
              ? "rounded-xl w-[60%] h-14 text-transparent bg-slate-300 animate-pulse"
              : ""
          )}
        >
          {loading && "Loading state City"}
          {!loading && (
            <h3 className="text-dark font-inter font-semibold text-level-5 tracking-[0.001em]">
              Category
            </h3>
          )}
          {!loading && (
            <div className="flex gap-[12px] flex-wrap overflow-x-auto w-auto tracking-[0.001em]">
              <SlidingTabs
                items={professionalDetails}
                displayKey="categoryName"
                activeItem={activeCategory}
                onItemSelect={handleCategorySelect}
                swipeEnabled={true}
              />
            </div>
          )}
        </div>

        {/* Skills */}
        <div
          className={twMerge(
            "flex flex-col gap-1.5",
            loading
              ? "rounded-xl w-[60%] h-16 text-transparent bg-slate-300 animate-pulse"
              : ""
          )}
        >
          {loading && "Loading state subcities"}
          {!loading && (
            <h3 className="text-dark font-inter font-semibold text-level-5 tracking-[0.001em]">
              Skills
            </h3>
          )}
          {!loading && (
            <div className="flex gap-[12px] flex-wrap overflow-x-auto w-full tracking-[0.001em]">
              <SlidingTabs
                items={activeSkills}
                displayKey="skillName"
                swipeEnabled={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceProCard;
